import * as React from "react";

import { AppRoute, useOrgRoutes, useRoutes } from "@/routes";
import { ArrowLeft, MinusIcon, TestTube2Icon } from "lucide-react";
import { NavButton, NavGroupProvider } from "@/components/nav-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/Sidebar";
import { useMemo, useState } from "react";

import { Button } from "./ui/Button";
import { FeatureRequestModal } from "./FeatureRequestModal";
import { SidebarBrandHeader } from "./sidebar-brand-header";
import { DevSidebarSlot } from "@/dev/sidebar-slot";
import { Icon } from "@/components/ui/Icon";
import { InsightsDockResumeButton } from "./insights-dock-resume-button";
import { Link } from "react-router";
import { McpDetailSidebarNav } from "./mcp-detail-sidebar-nav";
import { GatewaySidebarNav } from "./gateway-sidebar-nav";
import { McpServerXSidebarNav } from "./mcp-server-x-sidebar-nav";
import { ProjectGuideSidebarCta } from "./project-guide-sidebar-cta";
import { PluginDetailSidebarNav } from "./plugin-detail-sidebar-nav";
import type { ProjectNavRoute } from "@/hooks/useProjectNavRoutes";
import { RequireScope } from "./require-scope";
import { Scope } from "@gram/client/models/components/rolegrant.js";
import { ScopeGatedNavGroup } from "@/components/scope-gated-nav-group";
import { SidebarNavSkeleton } from "./sidebar-nav-skeleton";
import { SidebarUserMenu } from "./sidebar-user-menu";
import { SkillDetailSidebarNav } from "./skill-detail-sidebar-nav";
import { Stack } from "@/components/ui/Stack";
import { Text } from "@/components/ui/Text";
import { TrialStatusCard } from "./trial-status-card";
import { cn } from "@/lib/utils";
import { useGetPeriodUsage } from "@gram/client/react-query/getPeriodUsage.js";
import { useNavArea } from "@/hooks/useNavArea";
import { useProductTier } from "@/hooks/useProductTier";
import { useProjectNavRoutes } from "@/hooks/useProjectNavRoutes";
import { useRBAC } from "@/hooks/useRBAC";
import { useSidebar } from "@/components/ui/Sidebar/sidebar-context";
import { useSlugs } from "@/contexts/Sdk";

/** A top-level navigation item gated by its required scopes. */
function ScopeGatedTopLevelItem({
  item,
  scope = ["project:read"],
  resourceId,
  projectId,
}: {
  item: AppRoute;
  scope?: Scope | Scope[];
  resourceId?: string;
  projectId?: string;
}) {
  return (
    <RequireScope
      scope={scope}
      resourceId={resourceId}
      projectId={projectId}
      level="section"
    >
      <SidebarMenuItem>
        <NavButton
          title={item.title}
          href={item.href()}
          active={item.active}
          Icon={item.Icon}
          stage={item.stage}
        />
      </SidebarMenuItem>
    </RequireScope>
  );
}

export function AppSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>): React.JSX.Element {
  const routes = useRoutes();
  const { orgSlug } = useSlugs();
  const { state } = useSidebar();
  // While grants reload (e.g. right after switching projects, when the query
  // cache is cleared), show a skeleton so the scope-gated nav doesn't flash empty.
  const { isLoading: rbacLoading } = useRBAC();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // useProjectNavRoutes owns feature-flag and scope decisions for both the
  // sidebar and command palette so the two surfaces cannot drift.
  const allNavRoutes = useProjectNavRoutes();
  const navAccess = useMemo(() => {
    const map = new Map<string, ProjectNavRoute>();
    for (const entry of allNavRoutes) map.set(entry.route.url, entry);
    return map;
  }, [allNavRoutes]);
  const isAssistantsEnabled = navAccess.has(routes.assistants.url);
  const isOrgMemoryEnabled = navAccess.has(routes.orgMemory.url);
  const isExploreEnabled = navAccess.has(routes.explore.url);
  const isRiskWatchdogEnabled = navAccess.has(routes.watchdog.url);
  const isUserSessionsEnabled = navAccess.has(routes.mcpSessions.url);
  const isAgentManagementEnabled = navAccess.has(routes.agents.url);
  const isSignalsIntelligenceEnabled = navAccess.has(
    routes.signalsIntelligence.url,
  );

  // Shared with the page-title eyebrow (Page.Eyebrow) so the sidebar group
  // highlight and the page header always agree on the area. "Organization"
  // labels org-level pages in the header but is not a sidebar group.
  const navArea = useNavArea();
  const activeGroup = navArea === "Organization" ? undefined : navArea;

  // Find the specific active route title for the sliding highlight. Shared with
  // the command palette via useProjectNavRoutes so the two stay in sync.
  const activeRoute = allNavRoutes.find((entry) => entry.route.active)?.route;
  const accessFor = (
    route: AppRoute,
  ): { scope?: Scope[]; resourceId?: string; projectId?: string } => {
    const entry = navAccess.get(route.url);
    return entry
      ? {
          scope: entry.scope.length > 0 ? entry.scope : undefined,
          resourceId: entry.resourceId,
          projectId: entry.projectId,
        }
      : { scope: ["project:read"] };
  };
  // In collapsed mode, sub-items are hidden — fall back to group highlight.
  // Top-level items (Home, Settings) have no activeGroup, so keep activeItem for those.
  const activeItem =
    state === "collapsed" && activeGroup ? undefined : activeRoute?.title;

  let sidebarContent: React.ReactNode;
  if (rbacLoading) {
    // Shaped like the real list below — 3 top-level items, the divider, the
    // 4 collapsed groups, then Settings — at the same spacing, so resolving
    // the grants swaps the rows out without shifting the nav.
    sidebarContent = (
      <SidebarNavSkeleton
        rows={8}
        divideAfter={3}
        className="gap-0.5 px-2 group-data-[collapsible=icon]:px-0"
      />
    );
  } else if (routes.mcp.add.active) {
    // Adding a server is a focused flow: no nav competing with the choice,
    // just the way back — in the same spot the detail sidebars put theirs.
    // Standard width, so arriving from the MCP index doesn't shift the page.
    sidebarContent = (
      <SidebarMenu className="gap-1 group-data-[collapsible=icon]:px-0">
        <SidebarMenuItem>
          {/* Rule matches the breadcrumb bar's on the other side of the pane
              boundary: same vertical padding and text size, so the two read as
              one line across the sidebar edge. */}
          <Link
            to={routes.mcp.href()}
            // -mt-2 cancels SidebarContent's 8px top padding so the row starts
            // flush against the sidebar header rule; py-3.5 then centres the
            // label in the band, landing its bottom border level with the
            // breadcrumb bar's across the pane boundary.
            className="text-foreground hover:bg-accent trans border-foreground/10 -mt-2 flex items-center gap-2 border-b px-4 py-3.5 text-sm hover:no-underline group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
          >
            <ArrowLeft className="size-4 shrink-0" strokeWidth={1.75} />
            <span className="truncate group-data-[collapsible=icon]:hidden">
              Back to all MCPs
            </span>
          </Link>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  } else if (routes.mcp.details.active) {
    sidebarContent = <McpDetailSidebarNav />;
  } else if (routes.mcp.x.active) {
    sidebarContent = <McpServerXSidebarNav />;
  } else if (routes.mcp.gateway.active) {
    sidebarContent = <GatewaySidebarNav />;
  } else if (routes.skills.detail.active) {
    sidebarContent = <SkillDetailSidebarNav />;
  } else if (routes.plugins.detail.active) {
    sidebarContent = <PluginDetailSidebarNav />;
  } else {
    sidebarContent = (
      <NavGroupProvider activeGroup={activeGroup} activeItem={activeItem}>
        <SidebarMenu className="gap-0.5 px-2 group-data-[collapsible=icon]:px-0">
          {/* Home — the org-scoped app (was the "Organization settings"
              footer action); the project's own landing page sits below it as
              "Project Overview". Scoped to match OrgSidebar's own Home item,
              so it only shows to users who can open the page it links to. */}
          <RequireScope
            scope={["org:read", "project:read", "org:admin"]}
            level="section"
          >
            <SidebarMenuItem>
              <NavButton
                title="Home"
                href={`/${orgSlug}`}
                Icon={(p) => <Icon {...p} name="building" />}
              />
            </SidebarMenuItem>
          </RequireScope>

          {/* Project overview — top-level, no group */}
          <ScopeGatedTopLevelItem
            item={routes.home}
            {...accessFor(routes.home)}
          />

          {/* Chat — top-level, no group; a full-page entry to the
                  Project Assistant alongside the docked composer */}
          <ScopeGatedTopLevelItem
            item={routes.chat}
            {...accessFor(routes.chat)}
          />

          {/* Divider: sets Home + Chat apart from the grouped nav below */}
          <li aria-hidden="true" className="my-2 px-1">
            <div className="border-border border-t" />
          </li>

          {/* Identity group */}
          <ScopeGatedNavGroup
            label="Identity"
            Icon={(p) => <Icon {...p} name="fingerprint" />}
            items={[
              { item: routes.identities, ...accessFor(routes.identities) },
              ...(isAgentManagementEnabled
                ? [{ item: routes.agents, ...accessFor(routes.agents) }]
                : []),
              ...(isUserSessionsEnabled
                ? [
                    {
                      item: routes.mcpSessions,
                      ...accessFor(routes.mcpSessions),
                    },
                  ]
                : []),
              {
                item: routes.remoteIdentityProviders,
                ...accessFor(routes.remoteIdentityProviders),
              },
            ]}
          />

          {/* MCP Gateway group */}
          <ScopeGatedNavGroup
            label="MCP Gateway"
            Icon={(p) => <Icon {...p} name="hammer" />}
            items={[
              { item: routes.mcp, ...accessFor(routes.mcp) },
              ...(isAssistantsEnabled
                ? [{ item: routes.assistants, ...accessFor(routes.assistants) }]
                : []),
              { item: routes.skills, ...accessFor(routes.skills) },
              { item: routes.plugins, ...accessFor(routes.plugins) },
              { item: routes.environments, ...accessFor(routes.environments) },
              { item: routes.playground, ...accessFor(routes.playground) },
            ]}
          />

          {/* Security and Policy group */}
          <ScopeGatedNavGroup
            label="Security and Policy"
            Icon={(p) => <Icon {...p} name="shield" />}
            items={[
              // Watchdog supersedes Risk Overview: exactly one of the two
              // shows, mirroring useProjectNavRoutes. Risk Events sits below
              // the landing surface in both modes.
              ...(isRiskWatchdogEnabled
                ? [{ item: routes.watchdog, ...accessFor(routes.watchdog) }]
                : [
                    {
                      item: routes.riskOverview,
                      ...accessFor(routes.riskOverview),
                    },
                  ]),
              { item: routes.riskEvents, ...accessFor(routes.riskEvents) },
              { item: routes.policyCenter, ...accessFor(routes.policyCenter) },
            ]}
          />

          {/* Observability group */}
          <ScopeGatedNavGroup
            label="Observability"
            Icon={(p) => <Icon {...p} name="eye" />}
            items={[
              { item: routes.costs, ...accessFor(routes.costs) },
              ...(isExploreEnabled
                ? [
                    { item: routes.explore, ...accessFor(routes.explore) },
                    {
                      item: routes.dashboards,
                      ...accessFor(routes.dashboards),
                    },
                  ]
                : []),
              { item: routes.insights, ...accessFor(routes.insights) },
              {
                item: routes.agentSessions,
                ...accessFor(routes.agentSessions),
              },
              ...(isSignalsIntelligenceEnabled
                ? [
                    {
                      item: routes.signalsIntelligence,
                      ...accessFor(routes.signalsIntelligence),
                    },
                  ]
                : []),
              ...(isOrgMemoryEnabled
                ? [{ item: routes.orgMemory, ...accessFor(routes.orgMemory) }]
                : []),
              { item: routes.logs, ...accessFor(routes.logs) },
            ]}
          />

          {/* Settings — top-level, no group */}
          <ScopeGatedTopLevelItem
            item={routes.settings}
            {...accessFor(routes.settings)}
          />
        </SidebarMenu>
      </NavGroupProvider>
    );
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      {/* Brand row only — the project switcher now lives in the page header. */}
      <SidebarBrandHeader homeHref={`/${orgSlug}`} />
      <SidebarContent className="pt-2">{sidebarContent}</SidebarContent>
      <SidebarFooter className="border-t">
        <FreeTierExceededNotification />
        {/* Every card here is conditional — the trial banner, the guide CTA,
            and the dock's resume button (absent while the dock is open). When
            they all stand down the wrapper renders empty, and its margin was
            left behind as a band of blank space above the user menu, so it
            hides itself instead. */}
        <div className="mb-2 flex flex-col gap-1.5 empty:hidden">
          <TrialStatusCard />
          <ProjectGuideSidebarCta />
          <InsightsDockResumeButton />
        </div>
        {DevSidebarSlot && <DevSidebarSlot />}
        <SidebarUserMenu />
      </SidebarFooter>
      <FeatureRequestModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title="Access Logs"
        description="Logs are available for Pro and Enterprise customers. Upgrade your account to access detailed logging and analytics for your tools."
        actionType="logs_page_access"
        icon={TestTube2Icon}
        accountUpgrade={true}
      />
    </Sidebar>
  );
}

const FreeTierExceededNotification = () => {
  const productTier = useProductTier();
  // Only fetch usage data for free-tier users — this notification is
  // irrelevant for paid/enterprise tiers and the request takes ~3s.
  const { data: usage } = useGetPeriodUsage(undefined, undefined, {
    throwOnError: false,
    enabled: productTier === "base",
  });
  const orgRoutes = useOrgRoutes();

  if (!usage || productTier !== "base") {
    return null;
  }

  if (
    usage.toolCalls > usage.includedToolCalls ||
    usage.servers > usage.includedServers
  ) {
    return (
      <PersistentNotification variant="error">
        <Stack direction="vertical" gap={3} className="h-full">
          <Text variant="subheading">Limits exceeded</Text>
          <Text small>
            Free tier limits exceeded. Upgrade to continue using the platform.
          </Text>
          <orgRoutes.billing.Link className="mt-auto w-full">
            <Button size="sm" className="w-full">
              Billing →
            </Button>
          </orgRoutes.billing.Link>
        </Stack>
      </PersistentNotification>
    );
  }

  return null;
};

const PersistentNotification = ({
  variant = "default",
  className,
  children,
}: {
  variant?: "default" | "warning" | "error";
  className?: string;
  children: React.ReactNode;
}) => {
  const [isMinimized, setIsMinimized] = React.useState(false);

  const variantClass = {
    default: "bg-card border-border",
    warning:
      "bg-warning-softest border-warning-foreground text-warning-foreground",
    error: "bg-destructive-softest border-destructive text-destructive",
  }[variant];

  const closeButton = (
    <Button
      variant="tertiary"
      size="md"
      className="absolute top-0 right-0 hover:bg-transparent"
      onClick={() => setIsMinimized(true)}
    >
      <MinusIcon className="h-4 w-4" />
    </Button>
  );

  let classes =
    "absolute bottom-2 left-1/2 h-[180px] w-[180px] -translate-x-1/2 p-4 border trans overflow-clip ";
  if (isMinimized) {
    classes +=
      "h-[12px] w-[12px] left-2 translate-x-0 cursor-pointer hover:scale-110";
  }

  return (
    <div
      className={cn(classes, variantClass, className)}
      onClick={() => setIsMinimized(false)}
    >
      {!isMinimized && children}
      {!isMinimized && closeButton}
      {isMinimized && (
        <Button
          variant="tertiary"
          size="md"
          className="flex h-full w-full items-center justify-center"
        >
          <Text>?</Text>
        </Button>
      )}
    </div>
  );
};
